import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, ShoppingCart, UtensilsCrossed, Users, CalendarDays,
    BarChart3, Settings, ChefHat, LogOut, Menu, Bell,
    UserCircle, ChevronDown, Package, Printer
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { api } from '../lib/api';
import { isBillingOperational } from '../lib/billing';

const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', to: '/admin' },
    { icon: ShoppingCart, label: 'Pedidos', to: '/admin/pedidos' },
    { icon: Printer, label: 'Impressao', to: '/admin/impressao', feature: 'printing' as const },
    { icon: UtensilsCrossed, label: 'Cardapio', to: '/admin/cardapio' },
    { icon: CalendarDays, label: 'Reservas', to: '/admin/reservas', feature: 'reservations' as const },
    { icon: Users, label: 'Clientes', to: '/admin/clientes' },
    { icon: Package, label: 'Equipe', to: '/admin/equipe' },
    { icon: BarChart3, label: 'Relatorios', to: '/admin/relatorios' },
    { icon: Settings, label: 'Configuracoes', to: '/admin/configuracoes' },
];

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [featureFlags, setFeatureFlags] = useState({
        hasReservationModule: true,
        printingModuleEnabled: true,
    });
    const { user, logout } = useAuthStore();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!user?.establishmentId) return;

        api.get(`/establishments/id/${user.establishmentId}`)
            .then(({ data }) => {
                setFeatureFlags({
                    hasReservationModule: data.settings?.hasReservationModule !== false,
                    printingModuleEnabled: data.settings?.printingModuleEnabled !== false,
                });
            })
            .catch((error) => {
                console.error('Failed to load admin feature flags', error);
            });
    }, [user?.establishmentId]);

    useEffect(() => {
        if (!user?.establishmentId) return;

        api.get(`/billing/establishment/${user.establishmentId}`)
            .then(({ data }) => {
                if (!isBillingOperational(data) && location.pathname !== '/admin/assinatura') {
                    navigate('/admin/assinatura', { replace: true });
                }
            })
            .catch((error) => {
                console.error('Failed to load billing gate', error);
            });
    }, [location.pathname, navigate, user?.establishmentId]);

    const visibleNavItems = useMemo(
        () => navItems.filter((item) => {
            if (item.feature === 'reservations') {
                return featureFlags.hasReservationModule;
            }

            if (item.feature === 'printing') {
                return featureFlags.printingModuleEnabled;
            }

            return true;
        }),
        [featureFlags],
    );

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const SidebarContent = () => (
        <div className="flex flex-col h-full">
            <div className={`flex items-center gap-3 px-4 py-5 border-b border-[#2A2A2A] ${collapsed ? 'justify-center' : ''}`}>
                <div className="w-9 h-9 bg-gradient-to-br from-primary to-primary-hover rounded-xl flex items-center justify-center flex-shrink-0 shadow-glow-sm">
                    <ChefHat className="w-5 h-5 text-white" />
                </div>
                {!collapsed && (
                    <div>
                        <span className="text-base font-bold text-white tracking-tight">Garçom Digital</span>
                        <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest">Admin</p>
                    </div>
                )}
            </div>

            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {visibleNavItems.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === '/admin'}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                            `sidebar-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`
                        }
                        title={collapsed ? item.label : undefined}
                    >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        {!collapsed && <span>{item.label}</span>}
                    </NavLink>
                ))}
            </nav>

            <div className={`p-3 border-t border-[#2A2A2A] ${collapsed ? 'flex justify-center' : ''}`}>
                {!collapsed ? (
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#1A1A1A] transition-colors cursor-pointer group">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <UserCircle className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-white truncate">{user?.name ?? 'Administrador'}</p>
                            <p className="text-[11px] text-muted-foreground truncate capitalize">{user?.role ?? 'owner'}</p>
                        </div>
                        <button onClick={handleLogout} title="Sair" className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-destructive">
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <button onClick={handleLogout} className="p-2 rounded-xl hover:bg-[#1A1A1A] text-muted-foreground hover:text-destructive transition-all" title="Sair">
                        <LogOut className="w-5 h-5" />
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className="flex h-screen bg-background overflow-hidden">
            <aside className={`hidden lg:flex flex-col bg-surface border-r border-[#2A2A2A] transition-all duration-300 flex-shrink-0 ${collapsed ? 'w-[68px]' : 'w-60'}`}>
                <SidebarContent />
            </aside>

            {mobileOpen && (
                <div className="lg:hidden fixed inset-0 z-50 flex">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
                    <aside className="relative w-64 bg-surface border-r border-[#2A2A2A] flex flex-col z-10 animate-slide-in-left">
                        <SidebarContent />
                    </aside>
                </div>
            )}

            <div className="flex-1 flex flex-col overflow-hidden">
                <header className="h-16 bg-surface border-b border-[#2A2A2A] flex items-center justify-between px-4 md:px-6 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-[#1A1A1A] text-muted-foreground hover:text-white transition-all">
                            <Menu className="w-5 h-5" />
                        </button>
                        <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:flex p-2 rounded-lg hover:bg-[#1A1A1A] text-muted-foreground hover:text-white transition-all" title="Recolher menu">
                            <Menu className="w-5 h-5" />
                        </button>
                        <h1 className="text-sm font-semibold text-muted-foreground hidden md:block">Operacao do Restaurante</h1>
                    </div>

                    <div className="flex items-center gap-2">
                        <button className="relative p-2 rounded-lg hover:bg-[#1A1A1A] text-muted-foreground hover:text-white transition-all">
                            <Bell className="w-5 h-5" />
                            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full animate-pulse-slow" />
                        </button>
                        <button className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#1A1A1A] transition-all">
                            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                                <UserCircle className="w-4 h-4 text-primary" />
                            </div>
                            <span className="text-sm font-medium text-white hidden md:block">{user?.name ?? 'Admin'}</span>
                            <ChevronDown className="w-4 h-4 text-muted-foreground hidden md:block" />
                        </button>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto bg-background p-4 md:p-6 animate-fade-in">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
